import React, { useState, useCallback, useContext } from 'react';
import { Container } from 'react-bootstrap';
import Common from '../../shared/Common';
import type { IRecurringOperation } from '../../../interfaces/IRecurringOperation';
import AddOrUpdateRecurringOperationModal from './AddOrUpdateRecurringOperationModal';
import RecurringOperationsTable from './RecurringOperationsTable';
import { DictionaryContext } from '../../../App';
import IncomeCalendarsSynchronizationsTable from './IncomeCalendarsSynchronizationsTable';
import type { IIncomeCalendarSynchronization } from '../../../interfaces/IIncomeCalendarSynchronization';
import AddOrUpdateIncomeCalendarsSynchronization from './AddOrUpdateIncomeCalendarsSynchronization';

import './style.css';
import PerformIncomeCalendarsSynchronizationModal from './PerformIncomeCalendarsSynchronizationModal';

const RecurringOperations = () => {
  const [recurringOperations, setRecurringOperations] = useState<IRecurringOperation[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(-1);
  const [selectedRecurringOperation, setSelectedRecurringOperation] = useState<IRecurringOperation | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const fetchIdRef = React.useRef(0);
  
  const [incomeCalendarSynchronizations, setIncomeCalendarSynchronizations] = useState<
    IIncomeCalendarSynchronization[]
  >([]);
  const [incomeCalendarSynchronizationsLoading, setIncomeCalendarSynchronizationsLoading] = useState(true);
  const [showIncomeCalendarSynchronizationModal, setShowIncomeCalendarSynchronizationModal] = useState(false);
  const [selectedIncomeCalendarSynchronizationRowId, setSelectedIncomeCalendarSynchronizationRowId] = useState(-1);
  const [selectedIncomeCalendarSynchronization, setSelectedIncomeCalendarSynchronization] =
    useState<IIncomeCalendarSynchronization | null>(null);
  const [fetchIncomeCalendarSynchronizationTrigger, setFetchIncomeCalendarSynchronizationTrigger] = useState(0);
  const fetchIncomeCalendarSynchronizationIdRef = React.useRef(0);
  const [showPerformIncomeCalendarSynchronizationModal, setShowPerformIncomeCalendarSynchronizationModal] = useState(false);
  
  const { categoriesNames, locationNames, incomeCategories, incomeQuantityTypes } = useContext(DictionaryContext);

  const fetchData = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);

    const recurringOperationsResponse = await Common.authorizedFetch('api/recurringOperations/getRecurringOperations');

    const recurringOperationsData = await recurringOperationsResponse.json();
    if (recurringOperationsData.success) {
      if (fetchId === fetchIdRef.current) {
        setRecurringOperations(recurringOperationsData.result.recurringOperations);
      }
      setSelectedRecurringOperation(null);
      setSelectedRowId(-1);
    }

    setLoading(false);
  }, []);

  const fetchIncomeCalendarSynchronizationsData = useCallback(async () => {
    const fetchIncomeCalendarSynchronizationId = ++fetchIncomeCalendarSynchronizationIdRef.current;
    setIncomeCalendarSynchronizationsLoading(true);

    const incomeCalendarSynchronizations = await Common.authorizedFetch(
      'api/calendarSynchronizations/getIncomeCalendarSynchronizations'
    );

    const incomeCalendarSynchronizationsData = await incomeCalendarSynchronizations.json();
    if (incomeCalendarSynchronizationsData.success) {
      if (fetchIncomeCalendarSynchronizationId === fetchIncomeCalendarSynchronizationIdRef.current) {
        setIncomeCalendarSynchronizations(incomeCalendarSynchronizationsData.result.incomeCalendarSynchronizations);
      }
      setSelectedIncomeCalendarSynchronization(null);
      setSelectedIncomeCalendarSynchronizationRowId(-1);
    }
    setIncomeCalendarSynchronizationsLoading(false);
  }, []);

  return (
    <Container className='mt-2'>
      <h2 className='mb-3' id='tabelLabel'>
        Operacje cykliczne
      </h2>
      <RecurringOperationsTable
        data={recurringOperations}
        loading={loading}
        fetchData={fetchData}
        fetchTrigger={fetchTrigger}
        setFetchTrigger={setFetchTrigger}
        setShowModal={setShowModal}
        selectedRowId={selectedRowId}
        setSelectedRowId={setSelectedRowId}
        selectedRecurringOperation={selectedRecurringOperation}
        setSelectedRecurringOperation={setSelectedRecurringOperation}
      />
      <IncomeCalendarsSynchronizationsTable
        data={incomeCalendarSynchronizations}
        loading={incomeCalendarSynchronizationsLoading}
        fetchData={fetchIncomeCalendarSynchronizationsData}
        fetchTrigger={fetchIncomeCalendarSynchronizationTrigger}
        setFetchTrigger={setFetchIncomeCalendarSynchronizationTrigger}
        setShowModal={setShowIncomeCalendarSynchronizationModal}
        setShowPerformModal={setShowPerformIncomeCalendarSynchronizationModal}
        selectedRowId={selectedIncomeCalendarSynchronizationRowId}
        setSelectedRowId={setSelectedIncomeCalendarSynchronizationRowId}
        selectedIncomeCalendarSynchronization={selectedIncomeCalendarSynchronization}
        setSelectedIncomeCalendarSynchronization={setSelectedIncomeCalendarSynchronization}
      />
      <AddOrUpdateRecurringOperationModal
        selectedRecurringOperation={selectedRecurringOperation}
        show={showModal}
        onHide={() => {
          setShowModal(false);
          setSelectedRecurringOperation(null);
          setSelectedRowId(-1);
        }}
        onSave={() => {
          setFetchTrigger(fetchTrigger + 1);
        }}
        locationsNames={locationNames}
        categoriesNames={categoriesNames}
        incomeCategories={incomeCategories}
      />
      <AddOrUpdateIncomeCalendarsSynchronization
        selectedIncomeCalendarSynchronization={selectedIncomeCalendarSynchronization}
        show={showIncomeCalendarSynchronizationModal}
        onHide={() => {
          setShowIncomeCalendarSynchronizationModal(false);
          setSelectedIncomeCalendarSynchronization(null);
          setSelectedIncomeCalendarSynchronizationRowId(-1);
        }}
        onSave={() => {
          setFetchIncomeCalendarSynchronizationTrigger(fetchIncomeCalendarSynchronizationTrigger + 1);
        }}
        locationsNames={locationNames}
        incomeQuantityTypes={incomeQuantityTypes}
        incomeCategories={incomeCategories}
      />
      <PerformIncomeCalendarsSynchronizationModal
        selectedIncomeCalendarSynchronization={selectedIncomeCalendarSynchronization}
        show={showPerformIncomeCalendarSynchronizationModal}
        onHide={() => {
          setShowPerformIncomeCalendarSynchronizationModal(false);
          setSelectedIncomeCalendarSynchronization(null);
          setSelectedIncomeCalendarSynchronizationRowId(-1);
        }}
        locationsNames={locationNames}
        incomeQuantityTypes={incomeQuantityTypes}
        incomeCategories={incomeCategories}
      />
    </Container>
  );
};

export default RecurringOperations;
